import {
	FinancialAnalysisGranularity,
	type FinancialAnalysisPeriodBucket,
} from "@pilatestime/shared-types";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchOrgMgmtFinancialAnalysisQueryOptions } from "@/api/orgMgmtFinancialAnalysis";
import { formatCurrency } from "@/components/billing/CurrencyDisplay";
import {
	computePresetRange,
	type DateRange,
	dateInputValueToIso,
	RangeFilters,
	type RangePreset,
} from "@/components/financial/RangeFilters";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { OrganizationManagementNavigation } from "@/routes/_auth/app/_app/org/$orgId/org-management/-components/OrganizationManagementNavigation";
import {
	FinancialAnalysisChart,
	type FinancialAnalysisDrilldownKind,
} from "./-components/FinancialAnalysisChart";
import { FinancialAnalysisCostList } from "./-components/FinancialAnalysisCostList";
import { FinancialAnalysisSalesList } from "./-components/FinancialAnalysisSalesList";

const DEFAULT_GRANULARITY = FinancialAnalysisGranularity.MONTH;
const DEFAULT_PRESET: RangePreset = "6m";

const DEFAULT_RANGE = computePresetRange(DEFAULT_PRESET);

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/org-management/financial-analysis/",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		context.queryClient.ensureQueryData(
			fetchOrgMgmtFinancialAnalysisQueryOptions(
				orgId,
				token,
				DEFAULT_GRANULARITY,
				DEFAULT_RANGE,
			),
		);

		return { userId, token, orgId };
	},
	component: FinancialAnalysisPage,
});

function FinancialAnalysisPage() {
	const { t } = useTranslation();
	const { locale } = useDateFormatter();
	const { token, orgId } = Route.useLoaderData();

	const [granularity, setGranularity] =
		useState<FinancialAnalysisGranularity>(DEFAULT_GRANULARITY);
	const [preset, setPreset] = useState<RangePreset>(DEFAULT_PRESET);
	const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
	const [validationError, setValidationError] = useState<string | null>(null);
	const [selection, setSelection] = useState<{
		bucket: FinancialAnalysisPeriodBucket;
		kind: FinancialAnalysisDrilldownKind;
	} | null>(null);
	const selectedBucket = selection?.bucket ?? null;

	const handleSelectBucket = (
		bucket: FinancialAnalysisPeriodBucket,
		kind: FinancialAnalysisDrilldownKind,
	) => {
		setSelection({ bucket, kind });
	};

	const { data, isLoading, isError } = useQuery(
		fetchOrgMgmtFinancialAnalysisQueryOptions(orgId, token, granularity, range),
	);

	const currentPeriodTotal = data?.buckets.at(-1)?.totalAmount ?? "0";
	const currency = data?.currency ?? "BRL";

	const handlePresetChange = (next: RangePreset) => {
		setPreset(next);
		setValidationError(null);
		setSelection(null);
		if (next !== "custom") {
			setRange(computePresetRange(next));
		}
	};

	const handleCustomFromChange = (value: string) => {
		if (!value) return;
		const nextFrom = dateInputValueToIso(value, false);
		const nextRange = { fromDate: nextFrom, toDate: range.toDate };
		applyCustomRange(nextRange);
	};

	const handleCustomToChange = (value: string) => {
		if (!value) return;
		const nextTo = dateInputValueToIso(value, true);
		const nextRange = { fromDate: range.fromDate, toDate: nextTo };
		applyCustomRange(nextRange);
	};

	const applyCustomRange = (nextRange: DateRange) => {
		if (
			new Date(nextRange.toDate).getTime() <=
			new Date(nextRange.fromDate).getTime()
		) {
			setValidationError(
				t("orgManagement.financialAnalysis.filters.invalidRange"),
			);
			return;
		}
		setValidationError(null);
		setPreset("custom");
		setSelection(null);
		setRange(nextRange);
	};

	const selectedPeriodLabel = useMemo(() => {
		if (!selectedBucket) return "";
		return formatPeriodLabel(
			selectedBucket.periodStart,
			selectedBucket.periodEnd,
			granularity,
			locale,
		);
	}, [selectedBucket, granularity, locale]);

	const netResult = Number(data?.netResultAllPeriods ?? 0);

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("orgManagement.financialAnalysis.title")}
				description={t("orgManagement.financialAnalysis.subtitle")}
				showBackButton={true}
				backButtonText={t("orgManagement.back")}
				hideOnMobile={false}
			/>

			<div
				className="flex-1 flex flex-col md:flex-row h-[calc(100vh-4rem)] min-h-0 overflow-hidden"
				data-testid="organization-management-financial-container"
			>
				<OrganizationManagementNavigation activeTab="financial-analysis" />

				<div
					className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto"
					data-testid="organization-management-financial-main"
				>
					<div className="space-y-4 max-w-6xl mx-auto">
						<RangeFilters
							preset={preset}
							range={range}
							validationError={validationError}
							onPresetChange={handlePresetChange}
							onFromChange={handleCustomFromChange}
							onToChange={handleCustomToChange}
							idPrefix="financial-analysis"
							leftSlot={
								<GranularityToggle
									value={granularity}
									onChange={(next) => {
										setGranularity(next);
										setSelection(null);
									}}
								/>
							}
						/>

						<div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
							<KpiCard
								label={t("orgManagement.financialAnalysis.currentPeriod")}
								value={formatCurrency(currentPeriodTotal, currency)}
							/>
							<KpiCard
								label={t("orgManagement.financialAnalysis.totalAllPeriods")}
								value={formatCurrency(data?.totalsAllPeriods ?? "0", currency)}
							/>
							<KpiCard
								label={t("orgManagement.financialAnalysis.totalCost")}
								value={formatCurrency(
									data?.totalCostAllPeriods ?? "0",
									currency,
								)}
							/>
							<KpiCard
								className="md:col-span-2"
								emphasized
								label={t("orgManagement.financialAnalysis.netResult")}
								value={formatCurrency(
									data?.netResultAllPeriods ?? "0",
									currency,
								)}
								toneClass={netResult < 0 ? "text-error" : "text-success"}
							/>
						</div>

						<Card>
							<CardContent className="p-3 md:p-4 space-y-4">
								{isLoading && (
									<div className="flex items-center justify-center h-64 text-base-content/60 text-sm">
										{t("orgManagement.financialAnalysis.loading")}
									</div>
								)}
								{isError && (
									<div className="flex items-center justify-center h-64 text-error text-sm">
										{t("orgManagement.financialAnalysis.error")}
									</div>
								)}
								{!isLoading && !isError && data && (
									<>
										<FinancialAnalysisChart
											buckets={data.buckets}
											granularity={granularity}
											currency={currency}
											selectedPeriodStart={
												selectedBucket?.periodStart ?? undefined
											}
											onSelectBucket={handleSelectBucket}
										/>
										{selection?.kind === "sales" && (
											<FinancialAnalysisSalesList
												orgId={orgId}
												token={token}
												fromDate={selection.bucket.periodStart}
												toDate={selection.bucket.periodEnd}
												periodLabel={selectedPeriodLabel}
												onClose={() => setSelection(null)}
											/>
										)}
										{selection?.kind === "costs" && (
											<FinancialAnalysisCostList
												orgId={orgId}
												token={token}
												fromDate={selection.bucket.periodStart}
												toDate={selection.bucket.periodEnd}
												periodLabel={selectedPeriodLabel}
												onClose={() => setSelection(null)}
											/>
										)}
									</>
								)}
							</CardContent>
						</Card>
					</div>
				</div>
			</div>
		</div>
	);
}

interface GranularityToggleProps {
	value: FinancialAnalysisGranularity;
	onChange: (next: FinancialAnalysisGranularity) => void;
}

function GranularityToggle({ value, onChange }: GranularityToggleProps) {
	const { t } = useTranslation();

	return (
		<div className="flex gap-1 md:gap-0 md:join" role="tablist">
			<button
				type="button"
				role="tab"
				aria-selected={value === FinancialAnalysisGranularity.WEEK}
				className={`btn btn-sm md:join-item ${
					value === FinancialAnalysisGranularity.WEEK
						? "btn-primary"
						: "btn-ghost"
				}`}
				onClick={() => onChange(FinancialAnalysisGranularity.WEEK)}
			>
				{t("orgManagement.financialAnalysis.granularity.week")}
			</button>
			<button
				type="button"
				role="tab"
				aria-selected={value === FinancialAnalysisGranularity.MONTH}
				className={`btn btn-sm md:join-item ${
					value === FinancialAnalysisGranularity.MONTH
						? "btn-primary"
						: "btn-ghost"
				}`}
				onClick={() => onChange(FinancialAnalysisGranularity.MONTH)}
			>
				{t("orgManagement.financialAnalysis.granularity.month")}
			</button>
		</div>
	);
}

interface KpiCardProps {
	label: string;
	value: string;
	toneClass?: string;
	className?: string;
	emphasized?: boolean;
}

function KpiCard({
	label,
	value,
	toneClass,
	className,
	emphasized,
}: KpiCardProps) {
	return (
		<Card className={className}>
			<CardContent className="p-3 md:p-4">
				<p className="text-xs md:text-sm text-base-content/60 truncate">
					{label}
				</p>
				<p
					className={`mt-1 ${
						emphasized ? "text-xl md:text-3xl" : "text-lg md:text-2xl"
					} font-semibold tabular-nums truncate ${toneClass ?? "text-base-content"}`}
				>
					{value}
				</p>
			</CardContent>
		</Card>
	);
}

function formatPeriodLabel(
	isoStart: string,
	isoEnd: string,
	granularity: FinancialAnalysisGranularity,
	locale: string,
): string {
	const start = new Date(isoStart);
	if (granularity === FinancialAnalysisGranularity.MONTH) {
		return start.toLocaleDateString(locale, {
			month: "long",
			year: "numeric",
		});
	}
	// Week: show "Mon DD – Mon DD" using the inclusive end (periodEnd is exclusive).
	const inclusiveEnd = new Date(
		new Date(isoEnd).getTime() - 24 * 60 * 60 * 1000,
	);
	const startLabel = start.toLocaleDateString(locale, {
		month: "short",
		day: "2-digit",
	});
	const endLabel = inclusiveEnd.toLocaleDateString(locale, {
		month: "short",
		day: "2-digit",
		year:
			start.getUTCFullYear() !== inclusiveEnd.getUTCFullYear()
				? "numeric"
				: undefined,
	});
	return `${startLabel} – ${endLabel}`;
}
